import React from "react";
import { Box, MenuItem, Select } from "@mui/material";
import type { SelectChangeEvent, SelectProps } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { ArrowDown2 } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { RADIUS } from "../../../theme/mixins";

interface StyledSelectProps extends Omit<SelectProps, "onChange"> {
  label?: string;
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  options: { label: string; value: string | number; icon?: string }[];
  mode?: "light" | "dark";
  placeholder?: string;
}

const CustomSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== "mode",
})<{ mode?: "light" | "dark" }>(({ theme, mode }) => ({
  borderRadius: RADIUS.input,
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.primary,
  fontWeight: 600,
  "& .MuiOutlinedInput-notchedOutline": {
    border:
      mode === "dark"
        ? "1px solid transparent"
        : `1px solid ${theme.palette.border.strong}`,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.border.grey,
  },
  // "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C7CDD4" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
    borderWidth: 1,
  },
  "& .MuiSelect-select": { color: theme.palette.text.primary },
  "& .MuiSelect-icon": { color: theme.palette.text.secondary },
}));

const SelectDropdownIcon: React.FC = () => {
  const theme = useTheme();
  return (
    <ArrowDown2
      size={18}
      color={theme.palette.text.secondary}
      style={{ marginRight: 12 }}
    />
  );
};

const StyledSelect: React.FC<StyledSelectProps> = ({
  label,
  options,
  onChange,
  mode = "dark",
  placeholder,
  value,
  ...props
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const resolvedPlaceholder = placeholder ?? t('components.fields.selectPlaceholder');

  return (
    <Box sx={{ width: "100%" }}>
      {label !== undefined && label !== "" && (
        <Box
          component="label"
          sx={{
            display: "block",
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "6px",
            color:
              mode === "dark"
                ? alpha(theme.palette.common.white, 0.82)
                : theme.palette.text.primary,
          }}
        >
          {label}
        </Box>
      )}

      <CustomSelect
        {...props}
        value={value ?? ""}
        mode={mode}
        fullWidth
        displayEmpty
        onChange={onChange}
        renderValue={(selected) => {
          const v = selected as string | number;
          if (v === "" || v === undefined || v === null) {
            return (
              <span
                style={{ color: theme.palette.text.disabled, fontWeight: 400 }}
              >
                {resolvedPlaceholder}
              </span>
            );
          }
          const opt = options.find((o) => String(o.value) === String(v));
          if (!opt) return String(v);
          if (!opt.icon) return opt.label;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="img"
                src={opt.icon}
                alt=""
                sx={{ width: 22, height: 22, objectFit: "contain", flexShrink: 0 }}
              />
              {opt.label}
            </Box>
          );
        }}
        IconComponent={SelectDropdownIcon}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              borderRadius: "12px",
              boxShadow: theme.customShadows.dropdown,
              maxHeight: 320,
              overflowY: "auto",
              "& .MuiMenuItem-root": {
                color: "#014A4E",
                fontWeight: 400,
                fontSize: "14px",
                "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  fontWeight: 600,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16),
                },
              },
            },
          },
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.icon ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: "6px",
                    backgroundColor: theme.palette.grey[200],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={opt.icon}
                    alt=""
                    sx={{ width: 20, height: 20, objectFit: "contain" }}
                  />
                </Box>
                {opt.label}
              </Box>
            ) : (
              opt.label
            )}
          </MenuItem>
        ))}
      </CustomSelect>
    </Box>
  );
};

export default StyledSelect;
